import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Light from "components/headers/light";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import Swal from "sweetalert2";
import { FaEdit, FaTrash, FaFileInvoice } from "react-icons/fa";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom"; // Import useNavigate hook
import './orders.css';  // Assure-toi que le chemin est correct
import { useTranslation } from 'react-i18next';
import AnnouncementBanner from "components/AnnouncementBanner";
import NotificationBell from "components/NotificationBell";

const Orders = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [retraite, setRetraite] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editedOrder, setEditedOrder] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const token = localStorage.getItem("authToken");
    const role = localStorage.getItem("role"); // Assuming role is saved in localStorage
    const navigate = useNavigate(); // Initialize navigate
    const { i18n } = useTranslation();
    const currentLang = i18n.language; // e.g. 'fr' or 'ar'

    const fetchAllOrders = useCallback(async () => {
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                return;
            }
            const response = await axios.get("  https://jolyaparis.cloud/api/supplier/get-ordersAdmin", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) {
                setOrders(response.data.orders);
                setRetraite(response.data.retraite);
            }
        } catch (error) {
            if (error.response?.status === 403) {
                localStorage.removeItem("authToken");
                localStorage.removeItem("user");
                localStorage.removeItem("role");
                Swal.fire("Compte non vérifié", error.response.data.message, "warning").then(() => {
                    navigate("/login");
                });
            } else if (error.response?.status === 401) {
                localStorage.removeItem("authToken");
                localStorage.removeItem("role");
                Swal.fire("Unauthorized", "Please log in again.", "error").then(() => {
                    navigate("/login");
                });
            } else {
                Swal.fire("Error", "Something went wrong.", "error");
            }
        }
    }, [navigate]);
console.log(orders);
    useEffect(() => {
        if (!token || (role !== "supplier" && role !== "influenceur")) {
            Swal.fire({
                icon: "error",
                title: t("Non_autorisé"),
                text: t("Connexion_fournisseur_requise"),
            }).then(() => {
                navigate("/login");
            });
            return;
        }
        fetchAllOrders();
    }, [token, role, navigate, t, fetchAllOrders]);
    const totalGains = orders.reduce((sum, order) => sum + (order.gain || 0), 0);
    const gainsLivre = orders
        .filter((order) => order.status === "livré")
        .reduce((sum, order) => sum + (order.gain || 0), 0);
    const gainsLivrepasRetraite = orders
        .filter((order) => order.status === "livré" && order.retraite === 0)
        .reduce((sum, order) => sum + (order.gain || 0), 0);
    const pendingOrders = orders.filter(order => order.status === "en attente").length;
    const completedOrders = orders.filter(order => order.status === "livré").length;
    const cancelledOrders = orders.filter(order => order.status === "annulé").length;
    const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total || 0), 0);
    const averageOrderValue = orders.length ? totalRevenue / orders.length : 0;
    const clientsServed = new Set(
        orders
            .map((order) => order.customer?.CustomerID || order.customer?.Phone || order.idsOrder)
            .filter(Boolean)
    ).size;
    const retraites1 = Number(retraite) || 0;
    const formatCurrency = (amount) => {
        const numericValue = Number(amount);
        if (!Number.isFinite(numericValue)) {
            return `0.00 ${t("currency")}`;
        }
        return `${numericValue.toFixed(2)} ${t("currency")}`;
    };
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const statusOptions = Array.from(new Set(orders.map((order) => order.status).filter(Boolean)));
    const getOrderUniqueId = (order) => String(order.orderId ?? order.idsOrder ?? "");

    const filteredOrders = orders.filter((order) => {
        const idValue = order.idsOrder ?? order.orderId ?? "";
        const matchesId = !normalizedSearch || idValue.toString().toLowerCase().includes(normalizedSearch);
        const matchesStatus = statusFilter === "all" || order.status === statusFilter;
        return matchesId && matchesStatus;
    });

    const selectedOrders = orders.filter((order) => selectedOrderIds.includes(getOrderUniqueId(order)));

    const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every((order) => selectedOrderIds.includes(getOrderUniqueId(order)));

    const toggleOrderSelection = (order) => {
        const orderId = getOrderUniqueId(order);
        setSelectedOrderIds((prev) =>
            prev.includes(orderId)
                ? prev.filter((id) => id !== orderId)
                : [...prev, orderId]
        );
    };

    const toggleSelectAllFiltered = () => {
        if (!filteredOrders.length) return;
        if (allFilteredSelected) {
            const filteredIds = new Set(filteredOrders.map((order) => getOrderUniqueId(order)));
            setSelectedOrderIds((prev) => prev.filter((id) => !filteredIds.has(id)));
        } else {
            const filteredIds = filteredOrders.map((order) => getOrderUniqueId(order));
            setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
        }
    };

    const ensureSelectionOrAlert = () => {
        if (!selectedOrders.length) {
            Swal.fire(t("Erreur"), t("orders_export.no_selection"), "warning");
            return false;
        }
        return true;
    };

    const buildProductSummary = (order) => {
        const summary = order.products?.map((p) => {
            const name = currentLang === "ar" ? (p.name?.ar || p.name?.fr || "") : (p.name?.fr || p.name?.ar || "");
            return `${name} (x${p.quantity || 0})`;
        }) || [];
        return summary.join("; ");
    };

    const handleExportSelectedToCsv = () => {
        if (!ensureSelectionOrAlert()) return;
        const headers = [
            "Order ID",
            "Customer Name",
            "Phone",
            "Address",
            "City",
            "Status",
            "Payment Status",
            "Gain",
            "Total",
            "Products"
        ];

        const rows = selectedOrders.map((order) => {
            const customer = order.customer || {};
            const values = [
                order.idsOrder ?? order.orderId ?? "",
                customer.CustomerName || "",
                customer.Phone || "",
                customer.Address || "",
                customer.City || "",
                order.status || "",
                order.paymentStatus || "",
                order.gain || "",
                order.total || "",
                buildProductSummary(order)
            ];
            return values
                .map((value) => {
                    const stringValue = value === undefined || value === null ? "" : String(value);
                    const escaped = stringValue.replace(/"/g, '""');
                    return `"${escaped}"`;
                })
                .join(",");
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `orders-export-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportSelectedToPdf = () => {
        if (!ensureSelectionOrAlert()) return;
        const doc = new jsPDF();

        selectedOrders.forEach((order, index) => {
            if (index > 0) doc.addPage();
            let cursorY = 20;

            doc.setFontSize(16);
            doc.text(`${t("ID_de_commande")}: ${order.idsOrder ?? order.orderId ?? ""}`, 14, cursorY);
            cursorY += 10;

            doc.setFontSize(12);
            doc.text(`${t("Nom_du_client")}: ${order.customer?.CustomerName || ""}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Numéro_de_Téléphone")}: ${order.customer?.Phone || ""}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Adresse")}: ${order.customer?.Address || ""}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Ville")}: ${order.customer?.City || ""}`, 14, cursorY);
            cursorY += 10;

            doc.text(`${t("Statut")}: ${order.status || ""}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Statut_Paiement")}: ${order.paymentStatus || ""}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Gains")}: ${formatCurrency(order.gain)}`, 14, cursorY);
            cursorY += 6;
            doc.text(`${t("Total")}: ${formatCurrency(order.total)}`, 14, cursorY);
            cursorY += 10;

            doc.text(t("Produits"), 14, cursorY);
            cursorY += 8;

            const products = order.products || [];
            if (!products.length) {
                doc.text(t("Aucun_produit_similaire_trouve"), 14, cursorY);
                cursorY += 6;
            } else {
                products.forEach((product, idx) => {
                    const name = currentLang === "ar"
                        ? (product.name?.ar || product.name?.fr || "")
                        : (product.name?.fr || product.name?.ar || "");
                    const priceAmount = Number(product.price);
                    const formattedPrice = Number.isFinite(priceAmount)
                        ? `${priceAmount.toFixed(2)} ${t("currency")}`
                        : `0.00 ${t("currency")}`;
                    doc.text(`${idx + 1}. ${name} x${product.quantity || 0} - ${formattedPrice}`, 14, cursorY);
                    cursorY += 6;
                });
            }
        });

        doc.save(`orders-export-${new Date().toISOString().slice(0, 10)}.pdf`);
    };
    const handleEdit = (order) => {
        setSelectedOrder(order);
        const noteValue = (order.Note ?? order.note ?? order.customer?.Note ?? "").slice(0, 120);
        setEditedOrder({
            CustomerID: order.customer?.CustomerID || "",
            Name: order.customer?.CustomerName || "",
            Phone: order.customer?.Phone || "",
            Address: order.customer?.Address || "",
            City: order.customer?.City || "",
            note: noteValue,
        });
        setIsEditModalOpen(true);
    };

    const handleDelete = async (orderId) => {
        Swal.fire({
            title: t("Etes_vous_sur"),
            text: t("Action_irreversible"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t("Oui_supprimer"),
            cancelButtonText: t("Annuler"),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");
                    await axios.delete(`  https://jolyaparis.cloud/api/supplier/orders/${orderId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    Swal.fire(t("Supprime"), t("Commande_supprimee"), "success");
                    fetchAllOrders();
                } catch (error) {
                    Swal.fire(t("Erreur"), t("Suppression_impossible"), "error");
                }
            }
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedOrder) return;
        const sanitizedNote = (editedOrder.note || "").slice(0, 120);
        const payload = {
            ...editedOrder,
            note: sanitizedNote,
            Note: sanitizedNote,
        };
        try {
            await axios.put(
                `  https://jolyaparis.cloud/api/supplier/orders/${selectedOrder.orderId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire(t("Succes"), t("Commande_modifiee"), "success");
            fetchAllOrders();
            setIsEditModalOpen(false);
        } catch (error) {
            Swal.fire(t("Erreur"), t("Modification_impossible"), "error");
        }
    };

    const handleWithdrawRequest = async (gain) => {
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                Swal.fire({
                    icon: "error",
                    title: t("Erreur"),
                    text: t("Token_introuvable"),
                });
                return;
            }

            const response = await axios.post(
                "  https://jolyaparis.cloud/api/withdraw",
                { gain },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: t("Succes"),
                    text: t("Retrait_succes"),
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: t("Echec"),
                    text: t("Retrait_echec"),
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("Erreur"),
                text: t("Retrait_erreur"),
            });
        }
    };

    const handleGenerateInvoice = (order) => {
        const doc = new jsPDF();
        const invoiceId = order.idsOrder || order.orderId || "";
        let cursorY = 20;

        doc.setFontSize(18);
        doc.text(`Invoice ${invoiceId}`, 14, cursorY);
        cursorY += 12;

        doc.setFontSize(12);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, cursorY);
        cursorY += 10;

        doc.text("Customer", 14, cursorY);
        cursorY += 8;
        doc.text(`Name: ${order.customer?.CustomerName || "N/A"}`, 14, cursorY);
        cursorY += 6;
        doc.text(`Phone: ${order.customer?.Phone || "N/A"}`, 14, cursorY);
        cursorY += 6;
        doc.text(`Address: ${order.customer?.Address || "N/A"}`, 14, cursorY);
        cursorY += 6;
        doc.text(`City: ${order.customer?.City || "N/A"}`, 14, cursorY);
        cursorY += 10;

        doc.text("Products", 14, cursorY);
        cursorY += 8;
        order.products?.forEach((p, index) => {
            const name = currentLang === "ar" ? p.name?.ar || p.name?.fr || "" : p.name?.fr || p.name?.ar || "";
            const priceAmount = Number(p.price);
            const formattedPrice = Number.isFinite(priceAmount)
                ? `${priceAmount.toFixed(2)} ${t("currency")}`
                : `0.00 ${t("currency")}`;
            doc.text(`${index + 1}. ${name} x${p.quantity || 0} - ${formattedPrice}`, 14, cursorY);
            cursorY += 6;
        });
        cursorY += 4;

        doc.text(`Subtotal: ${formatCurrency(order.total)}`, 14, cursorY);
        cursorY += 6;
        doc.text(`Gain: ${formatCurrency(order.gain)}`, 14, cursorY);
        cursorY += 6;
        doc.text(`Status: ${order.status || "N/A"}`, 14, cursorY);
        cursorY += 6;
        doc.text(`Payment Status: ${order.paymentStatus || "N/A"}`, 14, cursorY);
        cursorY += 6;

        doc.save(`invoice-${invoiceId || "order"}.pdf`);
    };
    // Check if the order should be disabled (grayed out)
    const isDisabled = (order) => order.status === "livré" && order.retraite === 1;
    return (
        <AnimationRevealPage>
            <Light />

            <br/>
                        <br/>

                                        <AnnouncementBanner/>

            <div className="d-flex">
                <section className="w-100 p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <h1 className="text-dark display-4">{t('Tableau_de_bord_commandes')}</h1>
                        <div className="d-flex flex-wrap gap-2">
                            <div style={{ minWidth: "200px" }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t('Rechercher_par_id')}
                                />
                            </div>
                            <div style={{ minWidth: "200px" }}>
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">{t("Tous_les_statuts")}</option>
                                    {statusOptions.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                            <button
                                className="btn"
                                style={{
                                    backgroundColor: "#000000",
                                    color: "white",
                                    border: "none",
                                    padding: "10px 20px",
                                    borderRadius: "5px",
                                }}
                                onClick={handleExportSelectedToCsv}
                            >
                                {t("orders_export.export_csv")}
                            </button>
                            <button
                                className="btn"
                                style={{
                                    backgroundColor: "#000000",
                                    color: "white",
                                    border: "none",
                                    padding: "10px 20px",
                                    borderRadius: "5px",
                                }}
                                onClick={handleExportSelectedToPdf}
                            >
                                {t("orders_export.export_pdf")}
                            </button>
                        </div>
                        <button 
className="btn" 
style={{ 
    backgroundColor: '#000000', 
    color: 'white', 
    border: 'none', 
    padding: '10px 20px', 
    fontSize: '16px', 
    borderRadius: '5px', 
    fontWeight: 'bold' 
}}                        onClick={() => handleWithdrawRequest(gainsLivrepasRetraite)}>
                           {t('Demander_retrait')}
                        </button>
                    </div>
                    <hr className="my-4" />

                    {/* Cards Section */}
                    <div className="row g-3 mb-4">
                        {[ 
  { title: t("Commandes_en_attente"), value: pendingOrders },
  { title: t("Commandes_livrees"), value: completedOrders },
  { title: t("Commandes_annulees"), value: cancelledOrders },
  { title: t("Revenu_total"), value: formatCurrency(totalRevenue) },
  { title: t("Total_gains"), value: formatCurrency(totalGains) },
  { title: t("Gains_commandes_livrees"), value: formatCurrency(gainsLivre) },
  { title: t("Gains_non_retires"), value: formatCurrency(gainsLivrepasRetraite) },
  { title: t("Total_retraites"), value: formatCurrency(retraites1) },
    { title: t("Clients_servis"), value: clientsServed },
    { title: t("Average_order_value"), value: formatCurrency(averageOrderValue) }
                        ].map((item, index) => (
                            <div className="col-md-4" key={index}>
                               <div className={`card shadow-sm text-white ${[
    "bg-primary-500",
    "bg-primary-400",
    "bg-primary-300",
    "bg-primary-200",
    "bg-primary-100",
    "bg-primary-600",
    "bg-primary-300",
    "bg-primary-400",
    "bg-primary-200",
    "bg-primary-500",
][index % 10]}`}>


                                    <div className="card-body text-center">
                                        <h5 className="card-title">{item.title}</h5>
                                        <p className="card-text display-6">{item.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Orders Table */}
                    <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                            <thead>
                                <tr>
                                   <th>
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected && filteredOrders.length > 0}
                                            onChange={toggleSelectAllFiltered}
                                        />
                                    </th>
                                   <th>{t("ID_de_commande")}</th>
<th>{t("Informations_client")}</th>
<th>{t("Produits")}</th>
<th>{t("Statut")}</th>
<th>{t("Statut_Paiement")}</th>
 <th> {t('Created_at')}</th>
<th>{t("Gains")}</th>
<th>{t("Total")}</th>
<th>{t("Actions")}</th>

                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map((order) => {
                                    const orderId = getOrderUniqueId(order);
                                    const isSelected = selectedOrderIds.includes(orderId);
                                    return (
                                    <tr
                                        key={order.orderId}
                                        style={{
                                            backgroundColor: isDisabled(order) ? "#f0f0f0" : "",
                                            opacity: isDisabled(order) ? 0.6 : 1,
                                            pointerEvents: isDisabled(order) ? "none" : "auto",
                                        }}
                                    >
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleOrderSelection(order)}
                                            />
                                        </td>
                                        <td>{order.idsOrder}</td>
                                        <td>
                                            <div>{order.customer?.CustomerName}</div>
                                            <div>{order.customer?.Phone}</div>
                                            <div>{order.customer?.Address}</div>
                                            <div>{order.customer?.City}</div>
                                        </td>
                                                                                <td>
      {order.products?.map(p => {
        const name = currentLang === 'ar' ? (p.name.ar || p.name.fr) : (p.name.fr || p.name.ar);
        return `${name} (x${p.quantity})`;
      }).join(", ")}
    </td>
                                        <td>{order.status}</td>
                                        <td>{order.paymentStatus}</td>
                                        <td>{new Date(order.created_at).toLocaleString()}</td>
                                        <td>{order.gain} {t("currency")}</td>
                                        <td>{Number(order.total) + 40} {t("currency")}</td>
                                        <td>
    <div className="d-flex flex-wrap gap-2">
        <button 
            className="btn btn-info"
            onClick={() => handleGenerateInvoice(order)}
        >
            <FaFileInvoice />
        </button>
        <button 
            className="btn btn-warning"
            onClick={() => handleEdit(order)}
            disabled={order.status === "livré"}
        >
            <FaEdit />
        </button>
        <button 
            style={{ backgroundColor: 'red' }}
            className="btn btn-danger"
            onClick={() => handleDelete(order.orderId)}
            disabled={order.status === "livré"}
        >
            <FaTrash style={{ color: 'white' }} />
        </button>
    </div>
</td>

                                    </tr>
                                );
                                })}
                            </tbody>
                        </table>
                    </div>
               {/* Modal for editing */}
               {isEditModalOpen && selectedOrder && (
                    <div className="modal show d-block" id="editModal" tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{t('Modifier_Commande')}</h5>
                                    <button type="button" className="btn-close" onClick={() => setIsEditModalOpen(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleSave}>
                                        <div className="mb-3">
                                            <label htmlFor="customerName" className="form-label">{t('Nom_du_client')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="customerName"
                                                value={editedOrder.Name}
                                                onChange={(e) => setEditedOrder({ ...editedOrder, Name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="customerPhone" className="form-label">{t('Numéro_de_Téléphone')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="customerPhone"
                                                value={editedOrder.Phone}
                                                onChange={(e) => setEditedOrder({ ...editedOrder, Phone: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="customerAddress" className="form-label">{t('Adresse')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="customerAddress"
                                                value={editedOrder.Address}
                                                onChange={(e) => setEditedOrder({ ...editedOrder, Address: e.target.value })}
                                                required
                                            />
                                        </div>
                                        {/* <div className="mb-3">
                                            <label htmlFor="customerCity" className="form-label">{t('Ville')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="customerCity"
                                                value={editedOrder.City}
                                                onChange={(e) => setEditedOrder({ ...editedOrder, City: e.target.value })}
                                                required
                                            />
                                        </div> */}
                                        <div className="mb-3">
                                            <label htmlFor="orderNote" className="form-label">{t('note')}</label>
                                            <textarea
                                                className="form-control"
                                                id="orderNote"
                                                rows={3}
                                                maxLength={120}
                                                value={editedOrder.note || ""}
                                                onChange={(e) =>
                                                    setEditedOrder({
                                                        ...editedOrder,
                                                        note: e.target.value.slice(0, 120),
                                                    })
                                                }
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary">{t('Sauvegarder')}</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    </AnimationRevealPage>
    );
};

export default Orders;
